// Color parsing for the storefront editor previews.
//
// Everything here takes `unknown` on purpose. The values arrive from merchant-authored
// style data (section styleProps, headerConfig, page configs) which the API stores as
// Dictionary<string, dynamic> / opaque JSON, so a "color" can legitimately come back as a
// number, an object, or a CSS function like rgb()/hsl()/var(). Anything unparseable
// yields null and the caller decides what to fall back to, instead of throwing.

export interface IRgb {
    r: number;
    g: number;
    b: number;
}

const clamp255 = (n: number) => Math.min(255, Math.max(0, Math.round(n)));

const hslToRgb = (h: number, s: number, l: number): IRgb => {
    const sat = s / 100;
    const lum = l / 100;
    const c = (1 - Math.abs(2 * lum - 1)) * sat;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    const [r1, g1, b1] =
        hp < 1 ? [c, x, 0] :
        hp < 2 ? [x, c, 0] :
        hp < 3 ? [0, c, x] :
        hp < 4 ? [0, x, c] :
        hp < 5 ? [x, 0, c] :
                 [c, 0, x];
    const m = lum - c / 2;
    return { r: clamp255((r1 + m) * 255), g: clamp255((g1 + m) * 255), b: clamp255((b1 + m) * 255) };
};

// Parses a CSS color into RGB, or null when the value isn't a color we can read
// (non-string, CSS variable, gradient, named color, empty).
export const parseColor = (value: unknown): IRgb | null => {
    if (typeof value !== 'string') return null;
    const color = value.trim();
    if (!color) return null;

    if (color.startsWith('#')) {
        const digits = color.slice(1);
        if (!/^[0-9a-fA-F]+$/.test(digits)) return null;
        // #rgb / #rgba -> expand each nibble; #rrggbb / #rrggbbaa -> take the first 6.
        const full = (digits.length === 3 || digits.length === 4)
            ? digits.slice(0, 3).split('').map((d) => d + d).join('')
            : (digits.length === 6 || digits.length === 8) ? digits.slice(0, 6) : null;
        if (full == null) return null;
        const bigint = parseInt(full, 16);
        return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
    }

    // rgb(1 2 3), rgb(1, 2, 3), rgba(1, 2, 3, .5), and percentage components.
    const rgb = color.match(/^rgba?\(([^)]+)\)$/i);
    if (rgb) {
        const parts = rgb[1].split(/[,/\s]+/).filter((p) => p !== '');
        if (parts.length < 3) return null;
        const channels = parts.slice(0, 3).map((p) => {
            const n = parseFloat(p);
            if (isNaN(n)) return NaN;
            return clamp255(p.trim().endsWith('%') ? (n / 100) * 255 : n);
        });
        if (channels.some(isNaN)) return null;
        return { r: channels[0], g: channels[1], b: channels[2] };
    }

    const hsl = color.match(/^hsla?\(([^)]+)\)$/i);
    if (hsl) {
        const parts = hsl[1].split(/[,/\s]+/).filter((p) => p !== '');
        if (parts.length < 3) return null;
        const [h, s, l] = parts.slice(0, 3).map((p) => parseFloat(p));
        if ([h, s, l].some(isNaN)) return null;
        return hslToRgb(h, s, l);
    }

    return null;
};

// Perceived luminance (0-255) of a color, or null when it can't be parsed.
export const colorLuminance = (value: unknown): number | null => {
    const rgb = parseColor(value);
    if (rgb == null) return null;
    return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
};

// True when the color reads as dark. Unparseable values fall back to `fallback`
// (default false = treat as light) rather than throwing.
export const isDarkColor = (color: unknown, fallback = false): boolean => {
    const luminance = colorLuminance(color);
    if (luminance == null) return fallback;
    return luminance < 128;
};

// Mirrors the storefront's readableOn (ColorHelpers): keep `preferred` when it separates from the
// background by >= 60 luminance, else a legible white/dark chosen from the background's luminance.
export const readableOn = (background?: unknown, preferred?: string): string => {
    const bgLum = colorLuminance(background);
    const prefLum = colorLuminance(preferred);
    if (preferred && bgLum != null && prefLum != null && Math.abs(bgLum - prefLum) >= 60) return preferred;
    return (bgLum ?? 255) < 128 ? '#ffffff' : '#1a1a1a';
};
